/**
 * LogoLockup - Eclipse Accounting full logo with mark + wordmark
 *
 * The complete brand lockup with eclipse mark and "Eclipse Accounting" text.
 * Use for auth screens, headers, emails, PDFs, and marketing surfaces.
 *
 * @param size - Controls mark height; text scales proportionally
 * @param variant - Layout direction: 'horizontal' (default) or 'vertical'
 * @param showTagline - Whether to show "Business Accounting" subtitle
 * @param className - Additional Tailwind classes
 */

'use client';

import React from 'react';
import { cn } from '@/lib/utils';
import { LogoMark } from './LogoMark';

interface LogoLockupProps {
  size?: 24 | 32 | 48 | 64 | 96 | number;
  variant?: 'horizontal' | 'vertical';
  colorScheme?: 'auto' | 'dark';
  showTagline?: boolean;
  className?: string;
  'aria-label'?: string;
}

export function LogoLockup({
  size = 32,
  variant = 'horizontal',
  colorScheme = 'auto',
  showTagline = false,
  className,
  'aria-label': ariaLabel = 'Eclipse Accounting logo',
}: LogoLockupProps) {
  const isHorizontal = variant === 'horizontal';
  // Smaller text relative to mark (55% instead of 75%) for subtler appearance
  const fontSize = Math.floor(size * 0.55);
  const taglineSize = Math.floor(fontSize * 0.5);

  return (
    <div
      className={cn(
        'flex items-center gap-2',
        isHorizontal ? 'flex-row' : 'flex-col items-start gap-2',
        className
      )}
      role="img"
      aria-label={ariaLabel}
    >
      <LogoMark size={size} colorScheme={colorScheme} aria-hidden={true} />

      <div className={cn('flex flex-col', !isHorizontal && 'items-center')}>
        <span
          className={
            colorScheme === 'dark'
              ? 'leading-none font-semibold tracking-tight text-white'
              : 'text-text-1 leading-none font-semibold tracking-tight dark:text-white'
          }
          style={{ fontSize: `${fontSize}px` }}
        >
          Eclipse Accounting
        </span>

        {showTagline && (
          <span
            className={
              colorScheme === 'dark'
                ? 'mt-1 leading-none text-white/70'
                : 'text-text-3 mt-1 leading-none dark:text-slate-300'
            }
            style={{ fontSize: `${taglineSize}px` }}
          >
            Business Accounting
          </span>
        )}
      </div>
    </div>
  );
}
